import React, { useState } from 'react';
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaGooglePlay,
  FaApple,
} from 'react-icons/fa';
import { trackPhoneCall, trackAppDownload, trackButtonClick } from '../utils/gtm';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import TermsOfServiceModal from './TermsOfServiceModal';
import FAQModal from './FAQModal';
import logo from '../assets/icon.png';
import './Footer.css';

const Footer = () => {
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isFAQModalOpen, setIsFAQModalOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 76;
      const elementPosition = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth',
      });
    }
  };

  // GTM: Track navigation clicks
  const handleNavClick = (sectionName) => {
    trackButtonClick(`Navigate to ${sectionName}`, 'Footer Navigation');
    scrollToSection(sectionName);
  };

  // GTM: Track phone call
  const handlePhoneClick = () => {
    trackPhoneCall();
  };

  // GTM: Track email click
  const handleEmailClick = () => {
    trackButtonClick('Email Contact', 'Footer Contact');
  };

  // GTM: Track app downloads
  const handleAppDownload = (source) => {
    trackAppDownload(source);
  };

  // GTM: Track social media clicks
  const handleSocialClick = (platform) => {
    trackButtonClick(`${platform} Social Link`, 'Footer Social Media');
  };

  // Handle legal document clicks
  const handlePrivacyClick = (e) => {
    e.preventDefault();
    trackButtonClick('Privacy Policy', 'Footer Legal');
    setIsPrivacyModalOpen(true);
  };

  const handleTermsClick = (e) => {
    e.preventDefault();
    trackButtonClick('Terms of Service', 'Footer Legal');
    setIsTermsModalOpen(true);
  };

  const handleFAQClick = (e) => {
    e.preventDefault();
    trackButtonClick('FAQ', 'Footer Legal');
    setIsFAQModalOpen(true);
  };

  const handleCookieClick = (e) => {
    e.preventDefault();
    trackButtonClick('Cookie Policy', 'Footer Legal');
    // You can implement cookie policy modal similarly if needed
    alert('Cookie policy coming soon!');
  };

  const currentYear = new Date().getFullYear();

  return (
    <>
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            {/* Company Info */}
            <div className="footer-section footer-brand">
              <div className="footer-logo">
                <div className="logo-icon"><img src={logo} alt='Mealzy Foods Logo'/></div>
                <div className="logo-text">
                  <h3>Mealzy Foods</h3>
                  <p>Good Food • Great Mood</p>
                </div>
              </div>
              <p className="footer-description">
                Your everyday food & veggie delivery app. Fresh, delicious, and
                wholesome meals delivered right to your doorstep with love and
                care.
              </p>
              <div className="social-links">
                <a 
                  href="https://www.facebook.com/people/Mealzy-Foods/61579220649096/#"
                  target="_blank"
                  className="social-link" 
                  aria-label="Facebook"
                  onClick={() => handleSocialClick('Facebook')}
                >
                  <FaFacebookF />
                </a>
                <a 
                  href="#" 
                  className="social-link" 
                  aria-label="Twitter"
                  onClick={() => handleSocialClick('Twitter')}
                >
                  <FaTwitter />
                </a>
                <a 
                  href="https://www.instagram.com/mealzyfoods/" 
                  target="_blank"
                  className="social-link" 
                  aria-label="Instagram"
                  onClick={() => handleSocialClick('Instagram')}
                >
                  <FaInstagram />
                </a>
                <a 
                  href="#" 
                  className="social-link" 
                  aria-label="LinkedIn"
                  onClick={() => handleSocialClick('LinkedIn')}
                >
                  <FaLinkedin />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="footer-section">
              <h4 className="footer-title">Quick Links</h4>
              <ul className="footer-links">
                <li>
                  <button onClick={() => handleNavClick('home')}>Home</button>
                </li>
                <li>
                  <button onClick={() => handleNavClick('features')}>
                    Features
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavClick('menu')}>Menu</button>
                </li>
                <li>
                  <button onClick={() => handleNavClick('process')}>
                    How it Works
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavClick('trust')}>Why Us</button>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div className="footer-section">
              <h4 className="footer-title">Our Services</h4>
              <ul className="footer-links">
                <li>
                  <span>Food Delivery</span>
                </li>
                <li>
                  <span>Vegetable Delivery</span>
                </li>
                <li>
                  <span>Bulk Orders</span>
                </li>
                <li>
                  <span>Custom Combos</span>
                </li>
                <li>
                  <span>Corporate Catering</span>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="footer-section">
              <h4 className="footer-title">Get in Touch</h4>
              <div className="contact-info">
                <div className="contact-item">
                  <FaPhone />
                  <a 
                    href="tel:+917841097358" 
                    onClick={handlePhoneClick}
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    +91 7841097358
                  </a>
                </div>
                <div className="contact-item">
                  <FaEnvelope />
                  <a 
                    href="mailto:mealzyfoods@gmail.com"
                    onClick={handleEmailClick}
                    style={{ color: 'inherit', textDecoration: 'none' }}
                    aria-label="Send email to Mealzy Foods"
                  >
                    mealzyfoods@gmail.com
                  </a>
                </div>
                <div className="contact-item">
                  <FaMapMarkerAlt />
                  <span>BLUE RIDGE <br/> Phase 1, Hinjawadi Rajiv Gandhi Infotech Park, Hinjawadi, Pimpri-Chinchwad, Maharashtra 411057</span>
                </div>
              </div>

              {/* App Download */}
              <div className="app-download">
                <p className="download-text">Download our app</p>
                <div className="download-buttons">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.prashantsableannapurnadosaj.app&hl=en"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="download-btn"
                    onClick={() => handleAppDownload('Footer - Play Store')}
                  >
                    <FaGooglePlay />
                    <div>
                      <small>GET IT ON</small>
                      <strong>Google Play</strong>
                    </div>
                  </a>
                  <a 
                    href="#" 
                    className="download-btn"
                    onClick={() => handleAppDownload('Footer - App Store')}
                  >
                    <FaApple />
                    <div>
                      <small>Download on the</small>
                      <strong>App Store</strong>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Bottom - Keep original anchor tag styling */}
          <div className="footer-bottom">
            <div className="footer-bottom-content">
              <p>&copy; {currentYear} Mealzy Foods. All rights reserved.</p>
              <div className="footer-links-bottom">
                <a 
                  href="#"
                  onClick={handlePrivacyClick}
                >
                  Privacy Policy
                </a>
                <a 
                  href="#"
                  onClick={handleTermsClick}
                >
                  Terms of Service
                </a>
                <a href="#" onClick={handleFAQClick}>FAQ's</a>
                {/* <a 
                  href="#"
                  onClick={handleCookieClick}
                >
                  Cookie Policy
                </a> */}
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Legal Modals */}
      <PrivacyPolicyModal 
        isOpen={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
      />
      <TermsOfServiceModal 
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />
      <FAQModal 
        isOpen={isFAQModalOpen} 
        onClose={() => setIsFAQModalOpen(false)} 
      />
    </>
  );
};

export default Footer;
